import { useEffect, useState } from "react";
import { cn } from "../../lib/utils.ts";

export interface LiveTickerProps {
  /** Rotating activity lines (already anonymized / consent-safe). */
  items: string[];
  /** Dwell time per line, ms. */
  intervalMs?: number;
  className?: string;
}

/**
 * Single-line live activity ticker with a pulse dot. JS-driven cycle (robust
 * even when CSS animation timelines pause in a backgrounded tab).
 */
export function LiveTicker({ items, intervalMs = 3400, className }: LiveTickerProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (items.length <= 1) return;
    const cycle = setInterval(() => {
      setVisible(false);
      const swap = setTimeout(() => {
        setIndex((p) => (p + 1) % items.length);
        setVisible(true);
      }, 240);
      return () => clearTimeout(swap);
    }, intervalMs);
    return () => clearInterval(cycle);
  }, [items.length, intervalMs]);

  if (items.length === 0) return null;

  return (
    <div className={cn("flex min-w-0 items-center gap-2.5", className)}>
      <span className="jod-live-dot" aria-hidden="true" />
      <span
        data-testid="ticker-line"
        aria-live="polite"
        className="min-w-0 flex-1 truncate text-[0.78rem] text-cream-2 transition-[opacity,transform] duration-200"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(4px)",
        }}
      >
        {items[index]}
      </span>
    </div>
  );
}
